import React, { useContext, useEffect, useState } from "react";
import Layout from "../../../components/Layout";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import UserContext from "../../../contexts/UserContext";
import ResetFilters from "../../../components/filters/ResetFilters";
import { useTranslation } from "react-i18next";
import SelectFilter from "../../../components/filters/SelectFilter";
import SearchFilter from "../../../components/filters/SearchFilter";
import { Link, useLocation } from "react-router-dom";
import CustomTooltip from "../../../components/CustomTooltip";
import { EyeOpen, PrintIcon } from "../../../assets/icons/Icons";
import ExportDataButton from "../../../components/ExportDataButton";
import useExportCSV from "../../../hooks/useExportCSV";
import FilterKioskOrders from "../../../api/kiosk-orders/filterKioskOrders";
import ViewOrder from "../../../components/sell-order/View";
import Chip from "../../../components/Chip";
import { formatDate } from "../../../utils/formatDate";

import { getReceipt } from "../../../api/sell-orders/getReceipt";
import usePrint from "../../../hooks/usePrint";
import { CentsToDollars } from "../../../utils/formatPrice";
import FilterShiftReports from "../../../api/shift-report/filterShiftReports";
import { downloadFile } from "../../../api/downloadFile";
import { ExportSellOrders } from "../../../api/sell-orders/exportSellOrders";
import FilterSellOrders from "../../../api/sell-orders/filterSellOrders";
import { useGlobalContext } from "../../../contexts/GlobalContext";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    showViewItem,
    setShowViewItem,
    selectedItem,
    setSelectedItem,
  } = useData(FilterKioskOrders);

  const { handlePrint } = usePrint(getReceipt);

          const { setPageTitle, setBreadcrumbs } = useGlobalContext();
            
          useEffect(() => {
              setPageTitle(t("Card Issuing"));
              setBreadcrumbs([t("Card Issuing"), t("Kiosk Report")]);
            
              return () => {
                setPageTitle("");
                setBreadcrumbs([]);
              };
                // eslint-disable-next-line
          }, []);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  const handleExportCSV = async () => {
    try {
      const response = await FilterKioskOrders(-1, queryParams);
      const ids = response.data.map((item) => item.id);
      await downloadFile(ExportSellOrders, { ids: ids }, "kiosk_reports.csv");
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <Layout title={t("Kiosk Report")} breadcrumbs={t("Card Issuing")}>
      {showViewItem && (
        <ViewOrder
          order={selectedItem}
          onClose={() => setShowViewItem(false)}
        />
      )}

      <DataTable
        buttons={
          <>
            <ExportDataButton
              className="btn btn-secondary btn-medium"
              onClick={handleExportCSV}
              label={t("Export CSV")}
              disabled={!data.data?.length}
            />
          </>
        }
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "user",
                    label: t("By user"),
                  },
                ]}
                defaultValue="order"
                placeholder={<div>{t("Search by")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        columns={[
          { header: t("Order Number"), accessor: "id" },
          { header: t("Date"), accessor: "created_at" },
          { header: t("Order Type"), accessor: "type" },
          { header: t("Order Status"), accessor: "status" },
          { header: t("Device ID"), accessor: "device_id" },
          { header: t("Total Purchase Amount"), accessor: "total_cost" },
          { header: t("Office"), accessor: "office_name" },
          { header: t("Action"), accessor: "actions" },
        ]}
        actions={(item) => (
          <div className="d-flex align-items-center">
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handlePrint({
                  requestParams: { id: item.id, type: "api-load" },
                  name: `order-${item.id}`,
                  width: "",
                  height: "",
                  download: false,
                });
              }}
            >
              <CustomTooltip
                icon={<PrintIcon />}
                message={t("Print Receipt")}
              />
            </Link>
            <Link
              to="#"
              className="edit-link"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItem(item);
                setShowViewItem(true);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
          </div>
        )}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "created_at":
              return formatDate(value);
            case "total_cost":
              return <p className='text-right'>{CentsToDollars(value)}</p>;
            case "status":
              let status = "inactive";
              if (value === "submitted" || value === "success" || value === "completed") {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
